import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, TextBox, Button, Card, CheckBox, Badge, Select } from '../../../components/common';
import { inquiryService } from '../../../api/services';

export default function Inquiry() {
  const { t } = useTranslation();
  const INQUIRY_CATEGORIES = [
    { value: 'reservation', label: t('info.inquiry_cat_reservation') },
    { value: 'change', label: t('info.inquiry_cat_change') },
    { value: 'payment', label: t('info.inquiry_cat_payment') },
    { value: 'service', label: t('info.inquiry_cat_service') },
    { value: 'partnership', label: t('info.inquiry_cat_partnership') },
    { value: 'other', label: t('info.inquiry_cat_other') },
  ];
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('accessToken');
  const [category, setCategory] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myInquiries, setMyInquiries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  // BugHerd Vietnamese QA #5: 부킹 → 서비스 지역 외 경유지 → /inquiry 진입 시
  // AddressSearch 가 sessionStorage 에 복귀 경로를 저장. submit 완료 화면에서 활용.
  const [bookingReturnPath, setBookingReturnPath] = useState(null);
  useEffect(() => {
    try {
      const p = sessionStorage.getItem('vvt_inquiry_return_to');
      if (p) setBookingReturnPath(p);
    } catch { /* sessionStorage 비활성 환경 — 무시 */ }
  }, []);

  const loadMyInquiries = useCallback(() => {
    if (!isLoggedIn) return;
    inquiryService.getMyList()
      .then((data) => setMyInquiries(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => {});
  }, [isLoggedIn]);

  useEffect(() => { loadMyInquiries(); }, [loadMyInquiries]);

  const formatInquiryDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = d.getHours();
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ampm = hh < 12 ? t('common.am', { defaultValue: '오전' }) : t('common.pm', { defaultValue: '오후' });
    const h12 = String(hh % 12 || 12).padStart(2, '0');
    return `${yyyy}. ${mm}. ${dd}. ${ampm} ${h12}:${mi}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) return alert(t('info.inquiry_alert_category'));
    if (!name.trim()) return alert(t('info.inquiry_alert_name'));
    if (!title.trim()) return alert(t('info.inquiry_alert_title'));
    if (!content.trim()) return alert(t('info.inquiry_alert_content'));
    if (!privacyAgreed) return alert(t('info.inquiry_alert_privacy'));

    setSubmitting(true);
    try {
      await inquiryService.create({
        category,
        user_name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        title: title.trim(),
        content: content.trim(),
      });
      setSubmitted(true);
      loadMyInquiries();
    } catch {
      alert(t('info.inquiry_alert_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-content mx-auto px-2.5 sm:px-5 py-10 sm:py-16">
          <Card variant="auth" className="max-w-form mx-auto text-center">
            <div className="py-4">
              <div className="w-14 h-14 bg-mono-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="person" size={28} className="text-mono-400" />
              </div>
              <h3 className="text-lg font-bold text-mono-900 mb-2">{t('info.inquiry_login_required_title')}</h3>
              <p className="text-sm text-mono-500 mb-6">
                {t('info.inquiry_login_required_desc')}
              </p>
              <div className="flex gap-3 justify-center">
                <Button level="level3" onClick={() => navigate(-1)}>{t('info.inquiry_back')}</Button>
                <Link to="/login" state={{ from: { pathname: '/inquiry' } }}>
                  <Button level="level1">{t('info.inquiry_login')}</Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-content mx-auto px-2.5 sm:px-5 py-10 sm:py-16">
          <Card variant="auth" className="max-w-form mx-auto text-center">
            <div className="py-4">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="check-filled" size={28} className="text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-mono-900 mb-2">{t('info.inquiry_submitted_title')}</h3>
              <p className="text-sm text-mono-500 mb-6 whitespace-pre-line">
                {t('info.inquiry_submitted_desc')}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button level="level3" onClick={() => navigate('/faq')}>{t('info.inquiry_customer_center')}</Button>
                {bookingReturnPath && (
                  <Button
                    level="level2"
                    onClick={() => {
                      try { sessionStorage.removeItem('vvt_inquiry_return_to'); } catch { /* ignore */ }
                      navigate(bookingReturnPath);
                    }}
                  >
                    {t('info.inquiry_return_to_booking', { defaultValue: '예약 페이지로 돌아가기' })}
                  </Button>
                )}
                <Button level="level1" onClick={() => {
                  try { sessionStorage.removeItem('vvt_inquiry_return_to'); } catch { /* ignore */ }
                  navigate('/');
                }}>{t('info.inquiry_home')}</Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-content mx-auto px-4 sm:px-5 py-6 sm:py-10">
        <h1 className="text-xl font-semibold text-mono-900 mb-2 text-center">{t('info.inquiry_title')}</h1>
        <p className="text-sm text-mono-500 text-center mb-8">{t('info.inquiry_desc')}</p>

        {/* 내 문의 내역 (로그인 + 항목 있을 때만) */}
        {myInquiries.length > 0 && (
          <div className="max-w-form mx-auto mb-10">
            <h2 className="text-base font-semibold text-mono-900 mb-3 px-1">{t('info.inquiry_history_title')}</h2>
            <Card variant="auth" className="!p-0 overflow-hidden">
              {myInquiries.map((q, idx) => {
                const isOpen = expandedId === q.id;
                const isAnswered = q.status === 'answered';
                return (
                  <div key={q.id} className={idx > 0 ? 'border-t border-mono-100' : ''}>
                    <button
                      type="button"
                      onClick={() => setExpandedId(isOpen ? null : q.id)}
                      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-mono-50 transition"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="status" status={isAnswered ? 'answered' : 'pending'}>
                            {t(isAnswered ? 'info.inquiry_status_answered' : 'info.inquiry_status_pending')}
                          </Badge>
                          <span className="text-xs text-mono-400">{formatInquiryDate(q.created_at)}</span>
                        </div>
                        <p className="text-sm font-semibold text-mono-900 truncate">{q.title}</p>
                      </div>
                      <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={20} className="text-mono-400 shrink-0" />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-4 space-y-3">
                        <p className="text-sm text-mono-700 whitespace-pre-line break-words">{q.content}</p>
                        {q.answer && (
                          <div className="mt-3 pt-3 border-t border-mono-100">
                            <p className="text-xs font-bold text-orange-500 mb-1.5">{t('info.inquiry_answer_label')}</p>
                            <p className="text-sm text-mono-700 whitespace-pre-line break-words">{q.answer}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {/* 문의 작성 */}
        <div className="max-w-form mx-auto">
          <h2 className="text-base font-semibold text-mono-900 mb-3 px-1">{t('info.inquiry_form_title')}</h2>
          <Card variant="auth">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 문의 유형 */}
            <div>
              <label className="text-2sm font-medium text-mono-700 block mb-1.5">{t('info.inquiry_category')} <span className="text-red-500">*</span></label>
              <Select
                value={category}
                onChange={setCategory}
                placeholder={t('info.inquiry_category_placeholder')}
                options={INQUIRY_CATEGORIES}
              />
            </div>

            <TextBox label={t('info.inquiry_name')} placeholder={t('info.inquiry_name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} required />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextBox variant="phone" label={t('info.inquiry_phone')} value={phone} onChange={(v) => setPhone(v || '')} />
              <TextBox label={t('info.inquiry_email')} placeholder={t('info.inquiry_email_placeholder')} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <TextBox label={t('info.inquiry_inquiry_title')} placeholder={t('info.inquiry_title_placeholder')} value={title} onChange={(e) => setTitle(e.target.value)} required />

            <div>
              <label className="text-2sm font-medium text-mono-700 block mb-1.5">{t('info.inquiry_content')} <span className="text-red-500">*</span></label>
              {/* BugHerd #130: native HTML `required` 제거 — 빈 입력 submit 시 native 툴팁
                  ("Please fill out this field") 발생 방지. 검증은 handleSubmit 의 JS 로직이 담당. */}
              <textarea
                rows={5}
                placeholder={t('info.inquiry_content_placeholder')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full px-4 py-3 border border-mono-200 rounded-lg text-sm text-mono-900 placeholder:text-mono-400 focus:outline-none focus:border-orange-500 transition resize-none"
                aria-required="true"
              />
            </div>

            {/* BugHerd #130/#131: 개인정보 수집·이용 동의 — [필수] 텍스트 prefix 폐기 → CheckBox 의
                required prop 으로 별표(*) 자동 노출. i18n 키도 prefix 제거된 형태로 정리. */}
            <div className="border-t border-mono-100 pt-4 space-y-2">
              <CheckBox
                label={t('info.inquiry_privacy_consent')}
                required
                checked={privacyAgreed}
                onChange={() => setPrivacyAgreed((v) => !v)}
              />
              <div className="ml-9 text-xs text-mono-500 leading-relaxed break-keep">
                {t('info.inquiry_privacy_consent_desc')}{' '}
                <Link to="/policy/privacyConsent" target="_blank" rel="noreferrer" className="text-orange-500 underline">
                  {t('info.inquiry_privacy_view')}
                </Link>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" level="level3" fullWidth onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
              <Button type="submit" level="level1" fullWidth disabled={submitting || !privacyAgreed}>
                {submitting ? t('info.inquiry_submitting') : t('info.inquiry_submit')}
              </Button>
            </div>
          </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
